import { styled } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { ComponentProps, useEffect, useRef, useState } from 'react'
import { SetOptional } from 'type-fest'
import { LabeledValue } from 'ComponentLibrary/src/piazza/text'

type MetricGridProps = {
    numColumns: number
    values: SetOptional<LabeledValueProps, 'size'>[]
    variant?: 'spacious' | 'compact'
    color?: string
    size?: 'small' | 'medium' | 'large'
    height?: string
}

type LabeledValueProps = ComponentProps<typeof LabeledValue> & { endAdornment?: React.ReactElement }

export function MetricGrid({ numColumns, values, size = 'medium', variant = 'compact', height, ...props }: MetricGridProps) {
    const numItems = values.length
    const [gridWidth, setGridWidth] = useState(654)
    const gridRef = useRef<HTMLDivElement>(null)

    if (numItems % numColumns !== 0) {
        throw new NotMultipleError(`number of items (${numItems}) must be a multiple of columns (${numColumns})`)
    }

    useEffect(() => {
        const handleResize = () => {
            if (gridRef.current) {
                setGridWidth(gridRef.current.clientWidth)
            }
        }

        const resizeObserver = new ResizeObserver(handleResize)

        if (gridRef.current) {
            resizeObserver.observe(window.document.body)
        }

        return () => resizeObserver.disconnect()
    }, [])

    const maxCellWidth = `${(gridWidth - numColumns * 12 - 24) / numColumns}px`

    return (
        <Grid $columns={numColumns} ref={gridRef} {...props}>
            {values.map((value, index) => {
                const isNotLastRow = index < numItems - numColumns
                const isNotFirstRow = index >= numColumns
                const isNotFirstColumn = index % numColumns !== 0
                const isNotLastColumn = index % numColumns !== numColumns - 1
                return (
                    <OuterCellWrapper
                        key={`key-${value.value}-${value.unit}-${value.subheadline}`}
                        $hasSpacingBottom={isNotLastRow}
                        $hasSpacingLeft={isNotFirstColumn}
                        $hasSpacingRight={isNotLastColumn}
                    >
                        <InnerCellWrapper
                            $hasSpacingBottom={isNotLastRow}
                            $hasSpacingTop={isNotFirstRow}
                            $variant={variant}
                            $height={height}
                        >
                            <LabeledValue size={size} {...value} maxWidth={maxCellWidth} />
                        </InnerCellWrapper>
                    </OuterCellWrapper>
                )
            })}
        </Grid>
    )
}

const Grid = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $columns: number }>(({ $columns }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${$columns}, 1fr)`,
}))

const OuterCellWrapper = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $hasSpacingBottom: boolean; $hasSpacingRight: boolean; $hasSpacingLeft: boolean }>(({ $hasSpacingLeft, $hasSpacingRight }) => ({
    paddingRight: $hasSpacingRight ? tokens.SizingSpacing150 : undefined,
    paddingLeft: $hasSpacingLeft ? tokens.SizingSpacing150 : undefined,
    borderRight: $hasSpacingRight ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}` : undefined,
    display: 'flex',
    flexDirection: 'column',
}))

const InnerCellWrapper = styled('div')<{
    $hasSpacingBottom: boolean
    $hasSpacingTop: boolean
    $variant: 'spacious' | 'compact'
    $height?: number | string
}>(({ $hasSpacingBottom, $hasSpacingTop, $variant, $height }) => ({
    //TODO: this property should be removed, there is no variant prop in the Figma Piazza compoennt
    height: $height ?? ($variant === 'spacious' ? '102px' : '74px'),
    alignContent: 'center',
    paddingTop: $hasSpacingTop ? tokens.SizingSpacing150 : undefined,
    paddingBottom: $hasSpacingBottom ? tokens.SizingSpacing150 : undefined,
    borderBottom: $hasSpacingBottom ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}` : undefined,
}))

class NotMultipleError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'NotMultipleError'
    }
}
